import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import RobbieMark from './RobbieMark';
import usePresence from './usePresence';

/**
 * Robbie pop-up — a centred dialog while Robbie works. `small` is a quick
 * save (mark, title, one line, centred). `summary` is a long job: mark and
 * title in a row, then the body (a SegmentedProgress, a note) and actions —
 * the buyer can close it and the job carries on in a ProgressStrip.
 */
export type RobbiePopupSize = 'small' | 'summary';

export interface RobbiePopupProps {
  open: boolean;
  size?: RobbiePopupSize;
  title: React.ReactNode;
  sub?: React.ReactNode;
  /** Body under the header — progress, a note. */
  children?: React.ReactNode;
  /** Buttons, right-aligned (centred on `small`). */
  actions?: React.ReactNode;
  /** Shows the close button and closes on Escape / scrim click. */
  onClose?: () => void;
  /** The mark's state; `working` while the job runs, `done` once it lands. */
  markState?: 'working' | 'done';
}

export const RobbiePopup: React.FC<RobbiePopupProps> = ({
  open,
  size = 'summary',
  title,
  sub,
  children,
  actions,
  onClose,
  markState = 'working',
}) => {
  const { mounted, leaving } = usePresence(open);

  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;
  const small = size === 'small';
  const mark = <RobbieMark state={markState} size="md" aria-label={markState === 'done' ? 'Robbie is done' : 'Robbie is working'} />;

  return (
    <div
      className={`fixed inset-0 z-[80] flex items-center justify-center bg-chrome/40 p-s7 transition-opacity duration-200 ${
        leaving ? 'opacity-0' : 'opacity-100'
      }`}
      onMouseDown={(e) => onClose && e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-busy={markState === 'working'}
        className={`relative w-full rounded-[16px] bg-surface shadow-floating motion-safe:animate-wait-in ${
          small ? 'max-w-[340px] px-s7 pb-[22px] pt-s7 text-center' : 'max-w-[500px] px-[26px] pb-[22px] pt-s7 text-left'
        }`}
      >
        {onClose && (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-s5 top-s5 grid h-7 w-7 place-items-center rounded-control text-ink-muted hover:bg-neutral-bg hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        )}
        {small ? (
          <div className="flex flex-col items-center">
            <span className="mb-s3">{mark}</span>
            <p className="text-[17px] font-semibold leading-snug text-ink">{title}</p>
            {sub && <p className="mt-s1 text-body-md text-ink-secondary">{sub}</p>}
          </div>
        ) : (
          <div className="flex items-center gap-s5 pr-s7">
            {mark}
            <div className="min-w-0">
              <p className="text-[17px] font-semibold leading-snug text-ink">{title}</p>
              {sub && <p className="mt-[3px] text-body-md text-ink-secondary">{sub}</p>}
            </div>
          </div>
        )}
        {children && <div className="mt-s6 flex flex-col gap-s4">{children}</div>}
        {actions && (
          <div className={`mt-[20px] flex flex-wrap gap-s3 ${small ? 'justify-center' : 'justify-end'}`}>{actions}</div>
        )}
      </div>
    </div>
  );
};

export default RobbiePopup;
